import { useState } from "react";
import { Ruler, MessageCircleHeart, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RubricSummary, RubricEditor, QualityMix, WeekdayInsights } from "@/components/rubric/RubricParts";

export function RubricProposalCard({ habit, onAnswer }) {
  const p = habit.rubric_proposal;
  if (!p) return null;
  return (
    <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-3" data-testid={`rubric-proposal-${habit.id}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-800"><MessageCircleHeart className="w-3.5 h-3.5" /> {p.clinician_name} suggests measuring this differently</div>
      {p.note && <p className="text-sm text-stone-700 mt-1">"{p.note}"</p>}
      <div className="mt-2"><RubricSummary rubric={p.rubric} /></div>
      {p.rubric.counts_from && p.rubric.counts_from !== "showed_up" && <div className="text-[11px] text-stone-500 mt-1">Weekly goal counts {p.rubric.counts_from === "full" ? "Full only" : "Partial and up"}.</div>}
      <div className="flex gap-2 mt-3">
        <button data-testid={`rubric-proposal-accept-${habit.id}`} onClick={() => onAnswer(habit, p, true)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-700 text-white hover:bg-emerald-800"><Check className="w-3.5 h-3.5" /> Use this</button>
        <button data-testid={`rubric-proposal-decline-${habit.id}`} onClick={() => onAnswer(habit, p, false)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-stone-300 text-stone-700 hover:bg-stone-100"><X className="w-3.5 h-3.5" /> Keep mine</button>
      </div>
    </div>
  );
}

export function MeasurePanel({ habit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [why, setWhy] = useState("");
  const [source, setSource] = useState("client");
  const [busy, setBusy] = useState(false);

  const open = () => { setDraft({ ...habit.rubric, counts_from: habit.rubric.counts_from || "showed_up" }); setWhy(""); setSource("client"); setEditing(true); };
  const suggest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/habits/rubric/suggest", { title: habit.title, kind: habit.kind, category: habit.category });
      setDraft({ ...r.data.rubric, counts_from: draft?.counts_from || "showed_up" });
      setWhy(r.data.rubric.why || "");
      setSource(r.data.source);
    } catch (e) {
      toast("Couldn't get a suggestion right now — you can still set it by hand.");
    } finally { setBusy(false); }
  };
  const save = async () => {
    const { why: _w, version, proposed_by, set_at, ...rubric } = draft;
    try {
      await api.put(`/habits/${habit.id}/rubric`, { rubric, source });
      toast.success("Measurement updated. Every tier still counts.");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that rubric");
    }
  };

  return (
    <div className="mt-3" data-testid={`measure-panel-${habit.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-stone-500"><Ruler className="w-3 h-3" /> Measured</span>
        <RubricSummary rubric={habit.rubric} testid={`rubric-summary-${habit.id}`} />
        {!editing && <button data-testid={`rubric-edit-${habit.id}`} onClick={open} className="text-[11px] font-semibold text-amber-800 hover:text-amber-900">{habit.rubric.type === "yesno" ? "Measure it your way" : "Change"}</button>}
      </div>
      {habit.rubric.proposed_by === "clinician" && <div className="text-[11px] text-stone-500 mt-1">Set together with your coach.</div>}
      {habit.rubric_review?.status === "rejected" && <div className="text-[11px] text-amber-800 mt-1" data-testid={`rubric-review-note-${habit.id}`}>Your coach would like to revisit how this is measured{habit.rubric_review.note ? `: "${habit.rubric_review.note}"` : "."}</div>}
      <QualityMix mix={habit.tier_mix} testid={`quality-mix-${habit.id}`} />
      <WeekdayInsights insights={habit.insights} rubric={habit.rubric} testid={`weekday-insights-${habit.id}`} />
      {editing && draft && (
        <div className="mt-3 rounded-2xl bg-stone-50 border border-stone-200 p-4 space-y-3">
          <RubricEditor value={draft} onChange={(v) => { setDraft(v); setSource("client"); }} onSuggest={suggest} suggesting={busy} why={why} idPrefix={`rubric-${habit.id}`} />
          <div className="flex gap-2">
            <button data-testid={`rubric-save-${habit.id}`} onClick={save} className="amber-btn text-xs py-2">Save</button>
            <button data-testid={`rubric-cancel-${habit.id}`} onClick={() => setEditing(false)} className="text-xs font-semibold text-stone-500 hover:text-stone-800 px-3">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
